import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { ResponsiveBar } from '@nivo/bar';
import {
  color_shades_darkest,
  color_shades_darker,
  color_shades_light,
  chart_blue_2,
  chart_blue_3,
  chart_blue_4,
  chart_blue_5,
  chart_blue_6,
  chart_blue_7,
  chart_slate_1,
  chart_slate_2,
  color_text_link,
  voicify_spark_light,
  voicify_spark_medium,
  voicify_spark_lighter,
  voicify_spark_darker
} from '../../constants/colors';
import {
  nivoChartTheme,
  barChartDefaults,
  chartGlassBlues,
  tooltipStyles
} from '../../constants/chartTheme';

// Transfer reason enum-style keys (matching backend hierarchy - lower index = higher priority)
export enum TransferReason {
  // Priority 1 - Immediate
  IMMEDIATE_TRANSFER = 'IMMEDIATE_TRANSFER',

  // Priority 2 - Capability Limitation
  UNSUPPORTED_APPOINTMENT_TYPE = 'UNSUPPORTED_APPOINTMENT_TYPE',

  // Priority 3 - System Failure
  POS_CONNECTION_ISSUE = 'POS_CONNECTION_ISSUE',

  // Priority 4 - Payment
  PAYMENT_LINK_REFUSED = 'PAYMENT_LINK_REFUSED',

  // Priority 5 - Pricing
  PRICING_ISSUE = 'PRICING_ISSUE',

  // Priority 6 - End of Call
  END_OF_CALL_TRANSFER = 'END_OF_CALL_TRANSFER',

  // Priority 7-8 - FAQ
  FAQ_CONFIGURED_TRANSFER = 'FAQ_CONFIGURED_TRANSFER',

  // Priority 9 - Unsupported Request
  UNSUPPORTED_REQUEST = 'UNSUPPORTED_REQUEST',

  // Priority 10 - AI Issue
  AI_ISSUE = 'AI_ISSUE',

  // Priority 11 - Off Hours
  OFF_HOURS_TRANSFER = 'OFF_HOURS_TRANSFER',

  // Priority 12 - Customer Frustrated
  CUSTOMER_FRUSTRATED = 'CUSTOMER_FRUSTRATED',

  // Priority 13 - Caller Requested
  CALLER_REQUESTED_TRANSFER = 'CALLER_REQUESTED_TRANSFER',

  // Unknown/Undetermined
  UNKNOWN = 'UNKNOWN'
}

// Human-readable labels for transfer reasons
export const TRANSFER_REASON_LABELS: Record<TransferReason, string> = {
  [TransferReason.IMMEDIATE_TRANSFER]: 'Immediate Transfer',
  [TransferReason.UNSUPPORTED_APPOINTMENT_TYPE]: 'Unsupported Appointment Type',
  [TransferReason.POS_CONNECTION_ISSUE]: 'POS Connection Issue',
  [TransferReason.PAYMENT_LINK_REFUSED]: 'Payment Link Refused',
  [TransferReason.PRICING_ISSUE]: 'Pricing Issue',
  [TransferReason.END_OF_CALL_TRANSFER]: 'End of Call Transfer',
  [TransferReason.FAQ_CONFIGURED_TRANSFER]: 'FAQ Configured Transfer',
  [TransferReason.UNSUPPORTED_REQUEST]: 'Unsupported Request',
  [TransferReason.AI_ISSUE]: 'AI Issue',
  [TransferReason.OFF_HOURS_TRANSFER]: 'Off-Hours Transfer',
  [TransferReason.CUSTOMER_FRUSTRATED]: 'Customer Frustrated',
  [TransferReason.CALLER_REQUESTED_TRANSFER]: 'Caller Requested Transfer',
  [TransferReason.UNKNOWN]: 'Undetermined'
};

// Abbreviated labels for axis display
export const TRANSFER_REASON_SHORT_LABELS: Record<TransferReason, string> = {
  [TransferReason.IMMEDIATE_TRANSFER]: 'Immediate',
  [TransferReason.UNSUPPORTED_APPOINTMENT_TYPE]: 'Unsupported Appt',
  [TransferReason.POS_CONNECTION_ISSUE]: 'POS Issue',
  [TransferReason.PAYMENT_LINK_REFUSED]: 'Payment Refused',
  [TransferReason.PRICING_ISSUE]: 'Pricing Issue',
  [TransferReason.END_OF_CALL_TRANSFER]: 'End of Call',
  [TransferReason.FAQ_CONFIGURED_TRANSFER]: 'FAQ Transfer',
  [TransferReason.UNSUPPORTED_REQUEST]: 'Unsupported Req',
  [TransferReason.AI_ISSUE]: 'AI Issue',
  [TransferReason.OFF_HOURS_TRANSFER]: 'Off-Hours',
  [TransferReason.CUSTOMER_FRUSTRATED]: 'Frustrated',
  [TransferReason.CALLER_REQUESTED_TRANSFER]: 'Caller Request',
  [TransferReason.UNKNOWN]: 'Undetermined'
};

// Map transfer reasons to their planned/unplanned/undetermined category (based on backend)
// Note: boolean values map to Planned (true) / Unplanned (false)
// null values map to Undetermined
export const TRANSFER_REASON_IS_PLANNED: Record<TransferReason, boolean | null> = {
  [TransferReason.IMMEDIATE_TRANSFER]: true,
  [TransferReason.UNSUPPORTED_APPOINTMENT_TYPE]: true,
  [TransferReason.POS_CONNECTION_ISSUE]: false,
  [TransferReason.PAYMENT_LINK_REFUSED]: true,
  [TransferReason.PRICING_ISSUE]: true,
  [TransferReason.END_OF_CALL_TRANSFER]: false,
  [TransferReason.FAQ_CONFIGURED_TRANSFER]: true,
  [TransferReason.UNSUPPORTED_REQUEST]: true,
  [TransferReason.AI_ISSUE]: false,
  [TransferReason.OFF_HOURS_TRANSFER]: true,
  [TransferReason.CUSTOMER_FRUSTRATED]: false,
  [TransferReason.CALLER_REQUESTED_TRANSFER]: true,
  [TransferReason.UNKNOWN]: null  // Maps to Undetermined
};

// Transfer category types
export type TransferCategory = 'Immediate Transfers' | 'Planned Transfers' | 'Unplanned Transfers' | 'Planned' | 'Unplanned' | 'Undetermined';

// App breakdown for transfer reasons
export interface TransferAppBreakdown {
  appName: string;
  count: number;
  english?: number;
  spanish?: number;
}

// Data structure for a transfer reason with app breakdown
export interface TransferReasonData {
  value: number;
  apps: TransferAppBreakdown[];
}

// Data structure for stacked bar (original format)
export interface TransferCategoryData {
  category: TransferCategory;
  [key: string]: string | number | TransferReasonData; // category name + reason data (value + apps)
}

// Data structure for horizontal bar with glass styling
export interface TransferReasonBarData {
  reason: string;
  reasonKey: TransferReason;
  value: number;
  category: 'Planned' | 'Unplanned';
  fillColor: string;
  borderColor: string;
  colorIndex: number;
}

// Color palette for Planned transfers (blues)
const PLANNED_COLORS = [
  { fill: 'rgba(14, 133, 187, 0.75)', border: chart_blue_2 },
  { fill: 'rgba(27, 165, 220, 0.70)', border: chart_blue_3 },
  { fill: 'rgba(46, 184, 232, 0.65)', border: chart_blue_4 },
  { fill: 'rgba(68, 190, 236, 0.60)', border: chart_blue_5 },
  { fill: 'rgba(94, 200, 239, 0.55)', border: chart_blue_6 },
  { fill: 'rgba(120, 210, 243, 0.50)', border: chart_blue_7 },
  { fill: 'rgba(133, 211, 244, 0.45)', border: chart_blue_7 }
];

// Color palette for Unplanned transfers (slate/gray-blue tones)
const UNPLANNED_COLORS = [
  { fill: 'rgba(71, 85, 105, 0.75)', border: chart_slate_1 },
  { fill: 'rgba(100, 116, 139, 0.70)', border: chart_slate_2 },
  { fill: 'rgba(120, 130, 150, 0.65)', border: '#788296' },
  { fill: 'rgba(140, 150, 165, 0.60)', border: '#8c96a5' },
  { fill: 'rgba(160, 170, 180, 0.55)', border: '#a0aab4' },
  { fill: 'rgba(180, 185, 195, 0.50)', border: '#b4b9c3' }
];

// Color palette for Undetermined transfers (light blue tones from Voicify Studio)
const UNDETERMINED_COLORS = [
  { fill: 'rgba(133, 211, 244, 0.75)', border: voicify_spark_medium },
  { fill: 'rgba(190, 231, 249, 0.70)', border: voicify_spark_light },
  { fill: 'rgba(226, 243, 252, 0.65)', border: voicify_spark_lighter },
  { fill: 'rgba(241, 249, 254, 0.60)', border: '#d4f0fb' },
  { fill: 'rgba(245, 251, 254, 0.55)', border: '#e2f3fc' },
  { fill: 'rgba(250, 253, 255, 0.50)', border: '#f1f9fe' }
];

export interface TransfersChartProps {
  data: TransferCategoryData[];
  showAsPercentage?: boolean;
  height?: number | string;
  enableGridY?: boolean;
  axisBottomLegend?: string;
  axisLeftLegend?: string;
  showTotals?: boolean;
  showLegend?: boolean;
  margin?: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  /** Optional render function for legend tooltips */
  renderLegendTooltip?: (category: 'Planned' | 'Unplanned' | 'Undetermined') => React.ReactElement;
  /** Optional callback for export button click */
  onExportClick?: () => void;
  /** Optional text for export button */
  exportButtonText?: string;
  /** Optional callback when a bar segment is clicked */
  onBarClick?: (data: {
    reason: string;
    reasonKey: string;
    value: number;
    category: 'Planned' | 'Unplanned' | 'Undetermined';
    apps: TransferAppBreakdown[];
  }) => void;
  /** 
   * Chart layout type:
   * - 'horizontal-stacked': Groups by Planned/Unplanned with stacked reasons (default, used in healthcare)
   * - 'vertical-by-reason': Individual vertical bars for each transfer reason (used in ordering)
   */
  chartLayout?: 'horizontal-stacked' | 'vertical-by-reason';
  /** Whether to show the Planned/Unplanned/Undetermined category legend (default: true) */
  showCategoryLegend?: boolean;
  /** When true, indicates Spanish only filter is active - affects tooltip display */
  spanishOnly?: boolean;
  /** When true, shows language breakdown (EN/ES) in tooltips. When false, hides it entirely. */
  showLanguageBreakdown?: boolean;
}

const defaultMargin = {
  top: 10,
  right: 50,
  bottom: 40,
  left: 110
};

// Interface for grouped stacked bar data
interface GroupedBarData {
  category: 'Planned' | 'Unplanned' | 'Undetermined';
  [key: string]: string | number;
}

// Interface for reason metadata used in tooltips
interface ReasonMeta {
  fullLabel: string;
  shortLabel: string;
  reasonKey: string;
  category: 'Planned' | 'Unplanned' | 'Undetermined';
  fillColor: string;
  borderColor: string;
  apps: TransferAppBreakdown[];
}

export const TransfersChart: React.FC<TransfersChartProps> = ({
  data,
  showAsPercentage = false,
  height = 340,
  enableGridY = true,
  axisBottomLegend,
  axisLeftLegend,
  showTotals = false,
  showLegend = false,
  margin = defaultMargin,
  onExportClick,
  exportButtonText,
  renderLegendTooltip,
  onBarClick,
  chartLayout = 'horizontal-stacked',
  showCategoryLegend = true,
  spanishOnly = false,
  showLanguageBreakdown = true
}) => {
  // Mobile detection for responsive axis ticks
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 640 : false);
  // Small laptop detection for legend font scaling (same pattern as ChartTooltip)
  const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const isChromebook = screenWidth >= 1024 && screenWidth < 1280;
  const isSmallLaptop = screenWidth >= 1280 && screenWidth < 1440;
  const legendFontSize = isChromebook ? '9px' : isSmallLaptop ? '10px' : '12px';
  const legendFontWeight500 = isChromebook ? 400 : 500;
  // Tooltip font sizes — same scaling ladder as ChartTooltip.tsx
  const ttTitle = isChromebook ? '11px' : isSmallLaptop ? '12px' : '14px';
  const ttValue = isChromebook ? '14px' : isSmallLaptop ? '15px' : '18px';
  const ttSubtitle = isChromebook ? '10px' : isSmallLaptop ? '11px' : '12px';
  const ttBody = isChromebook ? '10px' : isSmallLaptop ? '11px' : '12px';
  const ttSmall = isChromebook ? '9px' : isSmallLaptop ? '10px' : '11px';
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const checkMobile = () => setIsMobile(window.innerWidth <= 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Build stacked bar data for grouped horizontal bars (Planned vs Unplanned only, Undetermined is just counted)
  const buildGroupedStackedData = (): {
    barData: GroupedBarData[];
    keys: string[];
    reasonMeta: Record<string, ReasonMeta>;
    categoryTotals: { planned: number; unplanned: number; undetermined: number };
  } => {
    const barData: GroupedBarData[] = [];
    const reasonMeta: Record<string, ReasonMeta> = {};
    let plannedTotal = 0;
    let unplannedTotal = 0;
    let undeterminedTotal = 0;

    // Process each category
    data.forEach(categoryItem => {
      const category = categoryItem.category as 'Planned' | 'Unplanned' | 'Undetermined';
      const colorPalette = category === 'Planned' ? PLANNED_COLORS : category === 'Unplanned' ? UNPLANNED_COLORS : UNDETERMINED_COLORS;

      // Extract reasons and their values for this category
      const reasons: { key: TransferReason; value: number; apps: TransferAppBreakdown[] }[] = [];
      Object.keys(categoryItem).forEach(key => {
        if (key !== 'category' && Object.values(TransferReason).includes(key as TransferReason)) {
          const reasonData = categoryItem[key] as TransferReasonData;
          // Support both new format (object with value/apps) and old format (number)
          const value = typeof reasonData === 'number' ? reasonData : reasonData?.value ?? 0;
          const apps = typeof reasonData === 'object' && reasonData?.apps ? reasonData.apps : [];
          if (value > 0) {
            reasons.push({ key: key as TransferReason, value, apps });
          }
        }
      });

      // Sort by value descending so largest segments are at the bottom (start of bar)
      reasons.sort((a, b) => b.value - a.value);

      // Create bar data entry for this category (only if it has reasons with data)
      if (reasons.length > 0) {
        // Create bar data entry for this category
        const barEntry: GroupedBarData = { category };

        reasons.forEach((reason, index) => {
          const shortLabel = TRANSFER_REASON_SHORT_LABELS[reason.key];
          barEntry[shortLabel] = reason.value;

          // Store metadata for tooltip
          const colorIndex = Math.min(index, colorPalette.length - 1);
          reasonMeta[shortLabel] = {
            fullLabel: TRANSFER_REASON_LABELS[reason.key],
            shortLabel,
            reasonKey: reason.key,
            category,
            fillColor: colorPalette[colorIndex].fill,
            borderColor: colorPalette[colorIndex].border,
            apps: reason.apps
          };
        });

        barData.push(barEntry);
      }

      // Always count totals for all categories
      reasons.forEach(reason => {
        if (category === 'Planned') {
          plannedTotal += reason.value;
        } else if (category === 'Unplanned') {
          unplannedTotal += reason.value;
        } else {
          undeterminedTotal += reason.value;
        }
      });
    });

    // Get all unique reason keys for the stacked bar
    const allKeys = new Set<string>();
    barData.forEach(entry => {
      Object.keys(entry).forEach(key => {
        if (key !== 'category') allKeys.add(key);
      });
    });

    return {
      barData,
      keys: Array.from(allKeys),
      reasonMeta,
      categoryTotals: { planned: plannedTotal, unplanned: unplannedTotal, undetermined: undeterminedTotal }
    };
  };

  const { barData, keys, reasonMeta, categoryTotals } = buildGroupedStackedData();
  const totalTransfers = categoryTotals.planned + categoryTotals.unplanned + categoryTotals.undetermined;

  // Calculate max value for axis and generate mobile-friendly tick values
  const horizontalMaxValue = useMemo(() => {
    let maxVal = 0;
    barData.forEach(row => {
      let rowTotal = 0;
      keys.forEach(key => {
        if (typeof row[key] === 'number') {
          rowTotal += row[key] as number;
        }
      });
      if (rowTotal > maxVal) maxVal = rowTotal;
    });
    return maxVal;
  }, [barData, keys]);

  // Generate fewer tick values for mobile (3-4 ticks max)
  const mobileTickValues = useMemo(() => {
    if (horizontalMaxValue === 0) return [0];
    // Round up to a nice number
    const niceMax = Math.ceil(horizontalMaxValue / 100) * 100;
    const step = Math.ceil(niceMax / 3 / 100) * 100; // 3 intervals
    const ticks: number[] = [];
    for (let i = 0; i <= niceMax; i += step) {
      ticks.push(i);
    }
    return ticks;
  }, [horizontalMaxValue]);

  // Build vertical bar data for individual bars per transfer reason (used for vertical-by-reason layout)
  const buildVerticalBarData = (): {
    verticalBarData: { reason: string; value: number; category: 'Planned' | 'Unplanned' | 'Undetermined' }[];
    verticalReasonMeta: Record<string, ReasonMeta>;
  } => {
    const verticalBarData: { reason: string; value: number; category: 'Planned' | 'Unplanned' | 'Undetermined' }[] = [];
    const verticalReasonMeta: Record<string, ReasonMeta> = {};

    // Collect all reasons across all categories
    const allReasons: { key: TransferReason; value: number; apps: TransferAppBreakdown[]; category: 'Planned' | 'Unplanned' | 'Undetermined' }[] = [];

    data.forEach(categoryItem => {
      const category = categoryItem.category as 'Planned' | 'Unplanned' | 'Undetermined';

      Object.keys(categoryItem).forEach(key => {
        if (key !== 'category' && Object.values(TransferReason).includes(key as TransferReason)) {
          const reasonData = categoryItem[key] as TransferReasonData;
          const value = typeof reasonData === 'number' ? reasonData : reasonData?.value ?? 0;
          const apps = typeof reasonData === 'object' && reasonData?.apps ? reasonData.apps : [];
          if (value > 0) {
            allReasons.push({ key: key as TransferReason, value, apps, category });
          }
        }
      });
    });

    // Sort by value descending
    allReasons.sort((a, b) => b.value - a.value);

    // Build bar data and metadata for each reason
    allReasons.forEach((reason, index) => {
      const shortLabel = TRANSFER_REASON_SHORT_LABELS[reason.key];
      const colorPalette = reason.category === 'Planned' ? PLANNED_COLORS : reason.category === 'Unplanned' ? UNPLANNED_COLORS : UNDETERMINED_COLORS;
      const colorIndex = Math.min(index % colorPalette.length, colorPalette.length - 1);

      verticalBarData.push({
        reason: shortLabel,
        value: reason.value,
        category: reason.category
      });

      verticalReasonMeta[shortLabel] = {
        fullLabel: TRANSFER_REASON_LABELS[reason.key],
        shortLabel,
        reasonKey: reason.key,
        category: reason.category,
        fillColor: colorPalette[colorIndex].fill,
        borderColor: colorPalette[colorIndex].border,
        apps: reason.apps
      };
    });

    return { verticalBarData, verticalReasonMeta };
  };

  const { verticalBarData, verticalReasonMeta } = chartLayout === 'vertical-by-reason' ? buildVerticalBarData() : { verticalBarData: [], verticalReasonMeta: {} };

  // Use appropriate metadata based on layout
  const activeReasonMeta = chartLayout === 'vertical-by-reason' ? verticalReasonMeta : reasonMeta;

  // Get color for a bar segment based on its key and category
  const getBarColor = (bar: any): string => {
    const meta = activeReasonMeta[bar.id] || activeReasonMeta[bar.indexValue];
    return meta?.fillColor || chartGlassBlues[0];
  };

  // Portal-based tooltip component that follows cursor
  const PortalTooltip: React.FC<{ id: string; value: number; indexValue: string; data: any }> = ({ id, value, indexValue }) => {
    const [position, setPosition] = React.useState({ x: 0, y: 0 });
    // For vertical layout, the reason is in indexValue; for horizontal, it's in id
    const meta = activeReasonMeta[id] || activeReasonMeta[indexValue];

    React.useEffect(() => {
      // Skip mouse tracking for mobile compact mode
      if (isMobile) return;

      const handleMouseMove = (e: MouseEvent) => {
        setPosition({ x: e.clientX, y: e.clientY });
      };

      document.addEventListener('mousemove', handleMouseMove);
      return () => document.removeEventListener('mousemove', handleMouseMove);
    }, [isMobile]);

    if (!meta) return null;

    const percentage = totalTransfers > 0 ? ((value / totalTransfers) * 100).toFixed(1) : '0';
    const categoryTotal = meta.category === 'Planned' ? categoryTotals.planned : meta.category === 'Unplanned' ? categoryTotals.unplanned : categoryTotals.undetermined;
    const categoryPercentage = categoryTotal > 0 ? ((value / categoryTotal) * 100).toFixed(1) : '0';

    // Compact mode for mobile - render inline simplified tooltip
    if (isMobile) {
      return (
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
            padding: '10px 14px',
            borderRadius: '10px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)',
            border: `1px solid ${color_shades_light}`,
            maxWidth: '200px',
            textAlign: 'center' as const
          }}
        >
          <div style={{ marginBottom: 4 }}>
            <span style={{ ...tooltipStyles.value, fontSize: '14px' }}>
              {value.toLocaleString()}
            </span>
            <span style={{ ...tooltipStyles.subtitle, marginLeft: 4, fontSize: '11px' }}>
              transfers
            </span>
          </div>
          <div style={{
            ...tooltipStyles.subtitle,
            fontSize: '10px',
            lineHeight: 1.3
          }}>
            {percentage}% of all • {meta.category}
          </div>
          {onBarClick && (
            <div style={{
              fontSize: '10px',
              color: color_text_link,
              fontStyle: 'italic',
              marginTop: 6
            }}>
              This view is limited on Mobile. Open on Desktop for full details.
            </div>
          )}
        </div>
      );
    }

    // Position tooltip to the left of cursor
    const tooltipWidth = 280;
    const padding = 15;

    const tooltipContent = (
      <div
        style={{
          position: 'fixed',
          left: position.x - tooltipWidth - padding,
          top: position.y - 10,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)',
          padding: '12px 16px',
          borderRadius: '10px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)',
          border: `1px solid ${color_shades_light}`,
          minWidth: '180px',
          zIndex: 99999,
          pointerEvents: 'none' as const
        }}
      >
        <div style={{ ...tooltipStyles.title, fontSize: ttTitle, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{meta.fullLabel}</span>
          {spanishOnly && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: voicify_spark_darker,
              color: 'white',
              fontSize: '9px',
              fontWeight: 700,
              padding: '2px 5px',
              borderRadius: '3px',
              letterSpacing: '0.5px',
              flexShrink: 0
            }}>ES</span>
          )}
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '6px'
        }}>
          <div
            style={{
              ...tooltipStyles.colorIndicator,
              backgroundColor: meta.fillColor,
              border: `2px solid ${meta.borderColor}`
            }}
          />
          <span style={{ ...tooltipStyles.value, fontSize: ttValue }}>
            {value.toLocaleString()}
          </span>
          <span style={{ ...tooltipStyles.subtitle, fontSize: ttSubtitle }}>
            transfers
          </span>
        </div>
        <div style={{ ...tooltipStyles.subtitle, fontSize: ttSubtitle }}>
          {percentage}% of all transfers
        </div>
        <div style={{
          fontSize: ttSmall,
          color: meta.category === 'Planned' ? chart_blue_2 : meta.category === 'Unplanned' ? chart_slate_1 : voicify_spark_medium,
          fontWeight: 500,
          marginTop: '8px',
          paddingTop: '8px',
          borderTop: `1px solid ${color_shades_light}`
        }}>
          {meta.category} Transfer • {categoryPercentage}% of {meta.category.toLowerCase()}
        </div>
        {meta.apps && meta.apps.length > 0 && (
          <div style={{
            borderTop: `1px solid ${color_shades_light}`,
            paddingTop: '8px',
            marginTop: '8px'
          }}>
            <div style={{
              fontSize: ttSmall,
              color: color_shades_darker,
              marginBottom: '6px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              By Location:
            </div>
            {[...meta.apps]
              .sort((a, b) => b.count - a.count)
              .slice(0, 5)
              .map((app, index) => {
                const hasLanguageBreakdown = showLanguageBreakdown && ((app.english !== undefined && app.english > 0) || (app.spanish !== undefined && app.spanish > 0));
                return (
                  <div
                    key={index}
                    style={{
                      padding: '3px 0',
                      fontSize: ttBody,
                      color: color_shades_darker,
                      display: 'flex',
                      justifyContent: 'space-between',
                      flexDirection: hasLanguageBreakdown && !spanishOnly ? 'column' : 'row'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <span style={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        marginRight: '8px'
                      }}>
                        {app.appName}
                      </span>
                      <span style={{
                        fontWeight: 600,
                        color: color_shades_darkest
                      }}>
                        {app.count}
                      </span>
                    </div>
                    {hasLanguageBreakdown && !spanishOnly && (
                      <div style={{
                        display: 'flex',
                        gap: '12px',
                        fontSize: ttSubtitle,
                        color: color_shades_darker,
                        marginTop: '2px',
                        paddingLeft: '8px'
                      }}>
                        {app.english !== undefined && app.english > 0 && (
                          <span>EN: {app.english}</span>
                        )}
                        {app.spanish !== undefined && app.spanish > 0 && (
                          <span style={{ color: chart_blue_2 }}>ES: {app.spanish}</span>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            {meta.apps.length > 5 && (
              <div style={{
                fontSize: ttSmall,
                color: color_shades_darker,
                fontStyle: 'italic',
                marginTop: '4px'
              }}>
                +{meta.apps.length - 5} more locations
              </div>
            )}
          </div>
        )}
        {onBarClick && (
          <div style={{
            borderTop: `1px solid ${color_shades_light}`,
            paddingTop: '8px',
            marginTop: '8px',
            fontSize: '11px',
            color: color_text_link,
            fontStyle: 'italic',
            textAlign: 'center'
          }}>
            Click to see more data
          </div>
        )}
      </div>
    );

    return ReactDOM.createPortal(tooltipContent, document.body);
  };

  // Custom tooltip wrapper for nivo
  const CustomTooltip = ({ id, value, indexValue, data: barData }: any) => {
    return <PortalTooltip id={id} value={value} indexValue={indexValue} data={barData} />;
  };

  return (
    <div style={{ height, display: 'flex', flexDirection: 'column' }}>
      {/* Category Legend with totals */}
      {showCategoryLegend && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginBottom: '12px',
          paddingLeft: margin.left,
          paddingRight: margin.right
        }}>
          <div style={{
            display: 'flex',
            gap: isChromebook ? '12px' : isSmallLaptop ? '16px' : '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '3px',
                backgroundColor: PLANNED_COLORS[0].fill,
                border: `2px solid ${PLANNED_COLORS[0].border}`
              }} />
              <span style={{ fontSize: legendFontSize, fontWeight: legendFontWeight500, color: color_shades_darker }}>
                Planned
              </span>
              {renderLegendTooltip && renderLegendTooltip('Planned')}
              <span style={{ fontSize: legendFontSize, fontWeight: 600, color: chart_blue_2 }}>
                {categoryTotals.planned}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '3px',
                backgroundColor: UNPLANNED_COLORS[0].fill,
                border: `2px solid ${UNPLANNED_COLORS[0].border}`
              }} />
              <span style={{ fontSize: legendFontSize, fontWeight: legendFontWeight500, color: color_shades_darker }}>
                Unplanned
              </span>
              {renderLegendTooltip && renderLegendTooltip('Unplanned')}
              <span style={{ fontSize: legendFontSize, fontWeight: 600, color: chart_slate_1 }}>
                {categoryTotals.unplanned}
              </span>
            </div>
            {categoryTotals.undetermined > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '3px',
                  backgroundColor: UNDETERMINED_COLORS[0].fill,
                  border: `2px solid ${UNDETERMINED_COLORS[0].border}`
                }} />
                <span style={{ fontSize: legendFontSize, fontWeight: legendFontWeight500, color: color_shades_darker }}>
                  Undetermined
                </span>
                {renderLegendTooltip && renderLegendTooltip('Undetermined')}
                <span style={{ fontSize: legendFontSize, fontWeight: 600, color: voicify_spark_medium }}>
                  {categoryTotals.undetermined}
                </span>
              </div>
            )}
          </div>
          {onExportClick && (
            <button
              onClick={onExportClick}
              style={{
                padding: '6px 14px',
                background: '#2C3D52',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#3a5068';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#2C3D52';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {exportButtonText || 'View Details'}
            </button>
          )}
        </div>
      )}

      {/* Stacked horizontal bar chart (default) */}
      {chartLayout === 'horizontal-stacked' && (
        <div style={{ flex: 1, minHeight: 0 }}>
          <ResponsiveBar
            data={barData}
            keys={keys}
            indexBy="category"
            layout="horizontal"
            margin={{ ...margin, top: 0 }}
            padding={0.4}
            valueScale={{ type: 'linear' }}
            indexScale={{ type: 'band', round: true }}
            colors={getBarColor}
            borderRadius={0}
            borderWidth={0}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              tickValues: isMobile ? mobileTickValues : undefined,
              legend: axisBottomLegend,
              legendPosition: 'middle',
              legendOffset: 32,
              renderTick: (tick) => (
                <g transform={`translate(${tick.x},${tick.y})`}>
                  <text
                    textAnchor="middle"
                    dominantBaseline="hanging"
                    style={{
                      fontSize: '10px',
                      fill: color_shades_darker,
                      fontWeight: 500
                    }}
                  >
                    {tick.value}
                  </text>
                </g>
              )
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 16,
              tickRotation: 0,
              renderTick: (tick) => (
                <g transform={`translate(${tick.x - 8},${tick.y})`}>
                  <text
                    textAnchor="end"
                    dominantBaseline="middle"
                    style={{
                      fontSize: '11px',
                      fill: tick.value === 'Planned' ? chart_blue_2 : tick.value === 'Unplanned' ? chart_slate_1 : voicify_spark_medium,
                      fontWeight: 600
                    }}
                  >
                    {tick.value}
                  </text>
                </g>
              )
            }}
            enableGridX={barChartDefaults.enableGridX}
            enableGridY={false}
            enableLabel={false}
            theme={nivoChartTheme}
            tooltip={CustomTooltip}
            onClick={(bar) => {
              if (onBarClick) {
                const meta = activeReasonMeta[bar.id as string];
                if (meta) {
                  onBarClick({
                    reason: meta.fullLabel,
                    reasonKey: meta.reasonKey,
                    value: bar.value as number,
                    category: meta.category,
                    apps: meta.apps
                  });
                }
              }
            }}
            role="img"
            ariaLabel="Transfers by reason grouped horizontal stacked bar chart"
            animate={barChartDefaults.animate}
            motionConfig={barChartDefaults.motionConfig}
          />
        </div>
      )}

      {/* Vertical bar chart by reason */}
      {chartLayout === 'vertical-by-reason' && (
        <div style={{ flex: 1, minHeight: 0 }}>
          <ResponsiveBar
            data={verticalBarData}
            keys={['value']}
            indexBy="reason"
            layout="vertical"
            margin={{ ...margin, top: 20, bottom: 80 }}
            padding={0.3}
            valueScale={{ type: 'linear' }}
            indexScale={{ type: 'band', round: true }}
            colors={(bar: any) => {
              const meta = activeReasonMeta[bar.indexValue];
              return meta?.fillColor || chartGlassBlues[0];
            }}
            borderRadius={4}
            borderWidth={0}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 0,
              tickPadding: 12,
              tickRotation: -45,
              legend: axisBottomLegend,
              legendPosition: 'middle',
              legendOffset: 70,
              renderTick: (tick) => {
                const meta = activeReasonMeta[tick.value as string];
                const fillColor = meta?.category === 'Planned' ? chart_blue_2 : meta?.category === 'Unplanned' ? chart_slate_1 : voicify_spark_medium;
                // Truncate label if longer than 12 characters
                const label = String(tick.value);
                const truncatedLabel = label.length > 12 ? `${label.substring(0, 10)}...` : label;
                return (
                  <g transform={`translate(${tick.x},${tick.y + 4})`}>
                    <text
                      textAnchor="end"
                      dominantBaseline="middle"
                      transform="rotate(-45)"
                      style={{
                        fontSize: '10px',
                        fill: fillColor,
                        fontWeight: 500
                      }}
                    >
                      {truncatedLabel}
                    </text>
                  </g>
                );
              }
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 8,
              tickRotation: 0,
              legend: axisLeftLegend,
              legendPosition: 'middle',
              legendOffset: -40,
              tickValues: 5
            }}
            enableGridX={false}
            enableGridY={barChartDefaults.enableGridY}
            enableLabel={false}
            theme={nivoChartTheme}
            tooltip={({ indexValue, value }) => {
              const meta = activeReasonMeta[indexValue as string];
              if (!meta) return null;
              return <PortalTooltip id={indexValue as string} value={value as number} indexValue={indexValue as string} data={{}} />;
            }}
            onClick={(bar) => {
              if (onBarClick) {
                const meta = activeReasonMeta[bar.indexValue as string];
                if (meta) {
                  onBarClick({
                    reason: meta.fullLabel,
                    reasonKey: meta.reasonKey,
                    value: bar.value as number,
                    category: meta.category,
                    apps: meta.apps
                  });
                }
              }
            }}
            role="img"
            ariaLabel="Transfers by reason vertical bar chart"
            animate={barChartDefaults.animate}
            motionConfig={barChartDefaults.motionConfig}
          />
        </div>
      )}
    </div>
  );
};

export default TransfersChart;
